'use client'

import { createContext, useContext } from 'react'

interface ItemListContextValue {
  itemListId: string
  itemListName: string
}

const ItemListContext = createContext<Partial<ItemListContextValue>>({})

// ---------------------------------------------------------------------------
// React Context — used by select_item consumers inside a list component
// ---------------------------------------------------------------------------

export function ItemListProvider({
  itemListId,
  itemListName,
  children,
}: ItemListContextValue & { children: React.ReactNode }) {
  return (
    <ItemListContext.Provider value={{ itemListId, itemListName }}>
      {children}
    </ItemListContext.Provider>
  )
}

/**
 * Consumes the item-list context set by a parent `<ItemListProvider>`.
 * Returns `{}` when used outside a provider (graceful fallback).
 */
export function useItemListContext(): Partial<ItemListContextValue> {
  return useContext(ItemListContext)
}

const STORAGE_KEY = 'gtm_item_list_context'

interface PersistedEntry {
  id: string
  name: string
  exp: number
}

// ---------------------------------------------------------------------------
// The localStorage key ("gtm_item_list_context") is written by a GTM
// tag that fires on select_item events. Writing in GTM (rather than in app
// code) ensures the persistence is properly gated by the user's consent
// state. The tag stores item_list_id/item_list_name per item so downstream
// events (e.g. view_item, add_to_cart) can attribute back to the originating
// list.
//
// Because the write side lives in GTM, any changes to the key name,
// entry shape, or expiry logic must be coordinated there.
// ---------------------------------------------------------------------------
export function getPersistedItemListContext(
  itemId: string
): Partial<ItemListContextValue> {
  if (typeof window === 'undefined') return {}

  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}

    const map = JSON.parse(raw) as Record<string, PersistedEntry>
    const entry = map[itemId]
    if (!entry) return {}

    if (entry.exp < Date.now()) return {}

    return {
      ...(entry.id && { itemListId: entry.id }),
      ...(entry.name && { itemListName: entry.name }),
    }
  } catch {
    return {}
  }
}
